import { useState } from 'react';

const phases = [
  {
    phase: 'Phase 1',
    period: 'Months 1–4',
    label: 'Foundation',
    color: '#7B2D8B',
    programs: [
      {
        title: 'Kano Community Epilepsy Champions',
        icon: 'ri-user-star-line',
        why: 'Trust-building and myth correction are foundational. Without community legitimacy, nothing else works.',
      },
    ],
    milestone: 'Network of trained champions across metropolitan Kano and selected peri-urban LGAs',
  },
  {
    phase: 'Phase 2',
    period: 'Months 3–7',
    label: 'Visibility',
    color: '#9333EA',
    programs: [
      {
        title: 'Seizure First Aid in Schools & Markets',
        icon: 'ri-first-aid-kit-line',
        why: 'Creates visible public value very quickly. Easy to brand, easy to count, directly tied to reduction of harm.',
      },
    ],
    milestone: 'Measurable number of people trained and institutions adopting seizure-response protocols',
  },
  {
    phase: 'Phase 3',
    period: 'Months 5–9',
    label: 'Relationships',
    color: '#7B2D8B',
    programs: [
      {
        title: 'Family Support Circles & Patient Navigation',
        icon: 'ri-team-line',
        why: 'Generates real beneficiary relationships and protects against awareness-only impact. Both Kano and Abuja.',
      },
    ],
    milestone: 'Active support circles in Kano and Abuja generating patient stories and care navigation outcomes',
  },
  {
    phase: 'Phase 4',
    period: 'Months 8–12',
    label: 'Systems',
    color: '#6D28D9',
    programs: [
      {
        title: 'Task-Shifted Epilepsy Access Pilot',
        icon: 'ri-hospital-line',
        why: 'Requires local legitimacy first. IKIRU must be seen as strengthening the system, not duplicating it.',
      },
      {
        title: 'Abuja Policy & Media Lab',
        icon: 'ri-megaphone-line',
        why: 'Higher-level advocacy is most effective once real evidence and stories are emerging from Kano.',
      },
      {
        title: 'Data & Mapping Desk',
        icon: 'ri-database-2-line',
        why: 'Runs in parallel from Phase 1, but formal reporting and mapping begins once data streams are established.',
      },
    ],
    milestone: 'IKIRU positioned as the leading epilepsy voice in Northern Nigeria with a national policy presence',
  },
];

export default function LaunchSequence() {
  const [activePhase, setActivePhase] = useState(0);
  const phase = phases[activePhase];

  return (
    <section className="py-24 bg-[#FAF7FC]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-4">
          <span className="text-xs font-semibold uppercase tracking-[4px] text-[#7B2D8B]">Rollout Plan</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 text-center mb-4">
          12-Month <span className="text-[#7B2D8B]">Launch Sequence</span>
        </h2>
        <p className="text-gray-500 text-center max-w-xl mx-auto mb-16 leading-relaxed">
          A realistic, sequenced approach — building local legitimacy before moving into higher-level advocacy and systems change.
        </p>

        {/* Phase tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {phases.map((p, i) => (
            <button
              key={p.phase}
              onClick={() => setActivePhase(i)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activePhase === i
                  ? 'bg-[#7B2D8B] text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#7B2D8B] hover:text-[#7B2D8B]'
              }`}
            >
              <span>{p.phase}</span>
              <span className={`text-xs font-normal ${activePhase === i ? 'text-purple-200' : 'text-gray-400'}`}>{p.period}</span>
            </button>
          ))}
        </div>

        {/* Phase content */}
        <div className="bg-white rounded-3xl border border-purple-100 overflow-hidden">
          <div className="p-8 md:p-10 border-b border-purple-100">
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-bold uppercase tracking-widest"
                style={{ backgroundColor: phase.color }}
              >
                {phase.phase} — {phase.label}
              </span>
              <span className="text-gray-400 text-sm">{phase.period}</span>
            </div>
            <div className="flex items-start gap-3 mt-4 bg-[#FAF7FC] rounded-xl p-4 border border-purple-100">
              <i className="ri-flag-line text-[#7B2D8B] text-lg mt-0.5 shrink-0"></i>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#7B2D8B] mb-1">Phase Milestone</p>
                <p className="text-gray-700 text-sm leading-relaxed">{phase.milestone}</p>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-6">Programs Launching This Phase</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {phase.programs.map((prog) => (
                <div key={prog.title} className="bg-[#FAF7FC] rounded-2xl p-6 border border-purple-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 mb-4">
                    <i className={`${prog.icon} text-[#7B2D8B] text-xl`}></i>
                  </div>
                  <h4 className="font-bold text-gray-900 text-sm mb-3">{prog.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{prog.why}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Positioning statement */}
        <div className="mt-12 text-center">
          <div className="inline-block bg-gradient-to-r from-[#7B2D8B] to-[#9333EA] rounded-2xl p-px">
            <div className="bg-white rounded-2xl px-8 py-6 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#7B2D8B] mb-3">IKIRU Positioning Statement</p>
              <p className="text-gray-700 text-base leading-relaxed font-medium">
                &ldquo;IKIRU is the bridge between stigma reduction, family support, and practical access to epilepsy care in Nigeria — speaking the language of dignity and safety in the North, and the language of evidence and policy in Abuja.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
